import { ChainedCommands, SingleCommands } from "@tiptap/core";
import { TextStyle } from "@tiptap/extension-text-style";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      /**
       * Set the font size
       */
      setFontSize: (size: string) => ReturnType;
      /**
       * Unset the font size
       */
      unsetFontSize: () => ReturnType;
    };
    lineHeight: {
      /**
       * Set the line height
       */
      setLineHeight: (lineHeight: string) => ReturnType;
      /**
       * Unset the line height
       */
      unsetLineHeight: () => ReturnType;
    };
    letterSpacing: {
      /**
       * Set the letter spacing
       */
      setLetterSpacing: (letterSpacing: string) => ReturnType;
      /**
       * Unset the letter spacing
       */
      unsetLetterSpacing: () => ReturnType;
    };
    fontWeight: {
      /**
       * Set the font weight
       */
      setFontWeight: (fontWeight: string | number) => ReturnType;
      /**
       * Unset the font weight
       */
      unsetFontWeight: () => ReturnType;
    };
    fontStyle: {
      /**
       * Set the font style (e.g., italic, normal)
       */
      setFontStyle: (fontStyle: string) => ReturnType;
      /**
       * Unset the font style
       */
      unsetFontStyle: () => ReturnType;
    };
  }
}

export const TextStyleExtended = TextStyle.extend({
  addAttributes() {
    return {
      // ...this?.parent?.(),
      ...(this as any).parent?.(), // Type assertion approach
      fontSize: {
        default: "16",
        parseHTML: (element: HTMLElement) =>
          element.style.fontSize.replace("px", ""),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes["fontSize"]) {
            return {};
          }
          return {
            style: `font-size: ${attributes["fontSize"]}px`,
          };
        },
      },
      lineHeight: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.lineHeight || null,
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes["lineHeight"]) {
            return {};
          }
          return {
            style: `line-height: ${attributes["lineHeight"]}`,
          };
        },
      },
      letterSpacing: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.style.letterSpacing.replace("px", ""),
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes["letterSpacing"]) {
            return {};
          }
          return {
            style: `letter-spacing: ${attributes["letterSpacing"]}px`,
          };
        },
      },
      fontWeight: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.fontWeight || null,
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes["fontWeight"]) {
            return {};
          }
          return {
            style: `font-weight: ${attributes["fontWeight"]}`,
          };
        },
      },
      fontStyle: {
        default: null,
        parseHTML: (element: HTMLElement) => element.style.fontStyle || null,
        renderHTML: (attributes: Record<string, any>) => {
          if (!attributes["fontStyle"]) {
            return {};
          }
          return {
            style: `font-style: ${attributes["fontStyle"]}`,
          };
        },
      },
    };
  },

  addCommands() {
    return {
      // ...this.parent?.(),
      ...(this as any).parent?.(), // Type assertion approach
      setFontSize:
        (fontSize: string) =>
        ({ commands }: { commands: SingleCommands }) => {
          return commands.setMark(this.name, { fontSize: fontSize });
        },
      unsetFontSize:
        () =>
        ({ chain }: { chain: () => ChainedCommands }) => {
          return chain()
            .setMark(this.name, { fontSize: null })
            .removeEmptyTextStyle()
            .run();
        },
      // Line Height Commands
      setLineHeight:
        (lineHeight: string) =>
        ({ commands }: { commands: SingleCommands }) => {
          return commands.setMark(this.name, { lineHeight: lineHeight });
        },
      unsetLineHeight:
        () =>
        ({ chain }: { chain: () => ChainedCommands }) => {
          return chain()
            .setMark(this.name, { lineHeight: null })
            .removeEmptyTextStyle()
            .run();
        },
      // Letter Spacing Commands
      setLetterSpacing:
        (letterSpacing: string) =>
        ({ commands }: { commands: SingleCommands }) => {
          return commands.setMark(this.name, { letterSpacing: letterSpacing });
        },
      unsetLetterSpacing:
        () =>
        ({ chain }: { chain: () => ChainedCommands }) => {
          return chain()
            .setMark(this.name, { letterSpacing: null })
            .removeEmptyTextStyle()
            .run();
        },
      // Font Weight Commands
      setFontWeight:
        (fontWeight: string | number) =>
        ({ commands }: { commands: SingleCommands }) => {
          return commands.setMark(this.name, { fontWeight: fontWeight });
        },
      unsetFontWeight:
        () =>
        ({ chain }: { chain: () => ChainedCommands }) => {
          return chain()
            .setMark(this.name, { fontWeight: null })
            .removeEmptyTextStyle()
            .run();
        },
      // Font Style Commands
      setFontStyle:
        (fontStyle: string) =>
        ({ commands }: { commands: SingleCommands }) => {
          return commands.setMark(this.name, { fontStyle: fontStyle });
        },
      unsetFontStyle:
        () =>
        ({ chain }: { chain: () => ChainedCommands }) => {
          return chain()
            .setMark(this.name, { fontStyle: null })
            .removeEmptyTextStyle()
            .run();
        },
    };
  },
});
